import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Card, CardHeader } from '../primitives/Card';
import { Icon } from '../primitives/Icon';
import { Tooltip } from '../primitives/Tooltip';

export interface SectionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Rendered behind an info affordance, as the reference dashboards do. */
  help?: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** "View all →" style link at the bottom-right of the card. */
  viewAll?: { label: string; onClick?: () => void; href?: string };
  bodyClassName?: string;
  padded?: boolean;
  headingLevel?: 2 | 3 | 4;
}

/**
 * The workhorse panel of every dashboard screen: eyebrow title, optional help,
 * a dense body and an optional trailing link. Used dozens of times per page, so
 * its padding and header rhythm are fixed rather than per-caller.
 */
export const SectionCard = forwardRef<HTMLDivElement, SectionCardProps>(function SectionCard(
  {
    title,
    help,
    description,
    actions,
    viewAll,
    bodyClassName,
    padded = true,
    headingLevel = 2,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <Card ref={ref} as="section" className={cn('overflow-hidden', className)} {...rest}>
      <CardHeader
        headingLevel={headingLevel}
        title={
          <span className="inline-flex items-center gap-1.5">
            {title}
            {help ? (
              <Tooltip content={help}>
                <button
                  type="button"
                  aria-label={`About ${title}`}
                  className={cn('rounded-sm text-content-muted hover:text-content', focusRing)}
                >
                  <Icon name="info" size={13} />
                </button>
              </Tooltip>
            ) : null}
          </span>
        }
        description={description}
        actions={actions}
      />
      <div className={cn('flex min-w-0 flex-1 flex-col', padded && 'p-4', bodyClassName)}>
        {children}
      </div>
      {viewAll ? (
        <div className="flex justify-end border-t border-line-subtle px-4 py-2">
          {viewAll.href ? (
            <a
              href={viewAll.href}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm text-dense-lg font-medium text-content-link hover:underline',
                focusRing,
              )}
            >
              {viewAll.label}
              <Icon name="arrow-right" size={13} />
            </a>
          ) : (
            <button
              type="button"
              onClick={viewAll.onClick}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm text-dense-lg font-medium text-content-link hover:underline',
                focusRing,
              )}
            >
              {viewAll.label}
              <Icon name="arrow-right" size={13} />
            </button>
          )}
        </div>
      ) : null}
    </Card>
  );
});
